import { useRef, useState } from "react"
import { CampaignFeatureConfig } from "../../interfaces/campaignConfig"
import apiClient from "../../axiosClient"
import { Alert, Box, Button, CircularProgress, Paper, Typography } from "@mui/material"
import ErrorDisplay from "../baseComponents/errorDisplay"

export default function StepTwoUploadValidate({
    feature,
    inputsState,
    file,
    setFile,
    isValid,
    setIsValid,
    setValidationMessage,
    onValidated,
}: {
    feature: CampaignFeatureConfig
    inputsState: Record<string, any>
    file: File | null
    setFile: (f: File | null) => void
    isValid: boolean | null
    setIsValid: (b: boolean | null) => void
    setValidationMessage: (m: string | null) => void
    onValidated: () => void
}) {
    const [isValidating, setIsValidating] = useState(false)
    const fileInputRef = useRef<HTMLInputElement | null>(null)
    const [validationError, setValidationError] = useState<{
        type: string;
        message: string;
        details: string | string[];
    } | null>(null)

    const isXlsxFile = (file?: File | null) => {
        if (!file) return false
        const nameOk = file.name.toLowerCase().endsWith('.xlsx')
        const mimeOk = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        return nameOk || mimeOk
    }

    const formatBytes = (bytes: number) => {
        if (bytes === 0) return '0 B'
        const k = 1024
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
        const i = Math.floor(Math.log(bytes) / Math.log(k))
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }

    const handleFileSelected = (f: File | null) => {
        // reset previous state
        setFile(null)
        setIsValid(null)
        setValidationMessage(null)
        setValidationError(null)

        if (!f) return
        if (!isXlsxFile(f)) {
            setValidationMessage('Only .xlsx files accepted')
            return
        }
        setFile(f)
    }

    const clearAll = () => {
        handleFileSelected(null)
        // clear the native input so selecting the same file later will trigger onChange
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    const validate = async () => {
        if (!file) return setValidationMessage('Please upload a .xlsx file')
        setIsValidating(true)
        setValidationError(null)
        try {
            let form: FormData
            form = feature.buildValidateForm(file, inputsState);

            const res = await apiClient.post(feature.validateEndpoint, form, { headers: { 'Content-Type': 'multipart/form-data' } })
            const msg = res.data?.message ?? 'Validation successful'
            setIsValid(true)
            setValidationMessage(msg)
            onValidated()
        } catch (err: any) {
            console.error('validate err', err)
            const resp = err?.response
            setIsValid(false)
            
            // Check if response has detailed error structure
            if (resp?.data?.type && resp?.data?.details) {
                setValidationError({
                    type: resp.data.type,
                    message: 'Please check the details below',
                    details: resp.data.details
                })
                setValidationMessage(null)
            } else {
            let msg = 'Validation failed'
            if (resp?.data) {
                if (typeof resp.data === 'string') msg = resp.data
                else if (resp.data.message) msg = resp.data.message
            } else if (err?.message) msg = err.message
            setValidationMessage(msg)
            }
        } finally {
            setIsValidating(false)
        }
    }

    return (
        <>
            {/* Show template field info */}
            {feature.TemplateInfoComponent && (
                <feature.TemplateInfoComponent inputsState={inputsState} />
            )}

            <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="subtitle1" gutterBottom>Upload & Validate</Typography>

                <Box
                onDrop={(e) => {
                    e.preventDefault()
                    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                        handleFileSelected(e.dataTransfer.files[0])
                        // also clear native input so future identical selections trigger onChange
                        if (fileInputRef.current) fileInputRef.current.value = ''
                    }
                }}
                onDragOver={(e) => e.preventDefault()}
                sx={{ border: '2px dashed', borderColor: '#ccc', borderRadius: 1, p: 2, textAlign: 'center', mb: 2, cursor: 'pointer' }}
            >
                <input
                    ref={fileInputRef}
                    id={`file-input-${feature.key}`}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                        const f = e.target.files && e.target.files[0] ? e.target.files[0] : null
                        handleFileSelected(f)
                        // reset native input so selecting the same file again triggers onChange next time
                        if (fileInputRef.current) fileInputRef.current.value = ''
                    }}
                />
                <label htmlFor={`file-input-${feature.key}`} style={{ cursor: 'pointer' }}>
                    <Typography variant="body2">Click to select a .xlsx file or drop it here</Typography>
                    <Typography variant="caption" display="block" color="textSecondary">Accepted: .xlsx only</Typography>
                </label>
            </Box>

            {/* File summary */}
            {file &&
                <Box mb={2}>
                    <Typography variant="body2">File: <strong>{file?.name ?? '—'}</strong></Typography>
                    <Typography variant="body2">Size: <strong>{file ? formatBytes(file.size) : '—'}</strong></Typography>
                </Box>
            }

            <Box display="flex" gap={2}>
                <Button variant="contained" onClick={validate} disabled={!file || isValidating}>
                    {isValidating ? <CircularProgress size={18} /> : 'Validate File'}
                </Button>
                <Button variant="outlined" onClick={clearAll}>Clear</Button>
            </Box>

            {isValid !== null && !validationError && (
                <Box mt={2}><Alert severity={isValid ? 'success' : 'error'}>{isValid ? 'Validation passed' : 'Validation failed'}</Alert></Box>
            )}

            {validationError && (
                <Box mt={2}>
                    <ErrorDisplay error={validationError} showDownloadButton={true} />
                </Box>
            )}
            </Paper>
        </>
    )
}
